import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './Chatbot.css';
import chatConfig from './ChatSetupJustin.json';
import useSpeechRecognition from './useSpeechRecognition';
import useSpeechSynthesis from './useSpeechSynthesis';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';
import SpeechControls from './SpeechControls';
import LoadingOverlay from './LoadingOverlay';
import Notification from './Notification';
import DragAndDropArea from './DragAndDropArea';
import WebcamComponent from './WebcamComponent';

const Chatbot = ({ setFilterCriteria }) => {
    const [prompt, setPrompt] = useState('');
    const [response, setResponse] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [notification, setNotification] = useState('');
    const [editableQA, setEditableQA] = useState({ question: '', answer: '' });
    const [isEditMode, setIsEditMode] = useState(false);
    const responseEndRef = useRef(null);
    const chatPopupRef = useRef(null);
    const [dragOver, setDragOver] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [isVisionEnabled, setIsVisionEnabled] = useState(false);
    const [hasGreeted, setHasGreeted] = useState(false);
    const [isSpeakingEnabled, setIsSpeakingEnabled] = useState(true);

    // Speech Recognition Hook
    const { supported: speechRecognitionSupported } = useSpeechRecognition(handleVoiceInput);

    // Speech Synthesis Hook
    const { speak, isSpeaking } = useSpeechSynthesis();

    // Handle voice input and set it as prompt
    function handleVoiceInput(transcribedText) {
        setPrompt(transcribedText);
        handleSubmit(null, transcribedText);
    }

    const handleInputChange = (e) => {
        setPrompt(e.target.value);
    };

    const toggleChat = () => {
        setIsOpen(!isOpen);
    };

    // Function to handle detection from webcam
    const handlePersonDetected = (detected) => {
        if (detected && !hasGreeted && isVisionEnabled) {
            setNotification('Person detected');
            setTimeout(() => setNotification(''), 3000);

            setIsSpeakingEnabled(true);

            speak(
                "Hi, I'm Justin Panther. You can type in the chat to communicate with me. You can toggle my Mute button if you want me to only type my responses.",
                () => {
                    setIsSpeakingEnabled(false);
                }
            );

            setHasGreeted(true);
            setIsVisionEnabled(false);
            setIsOpen(false);
        }
    };

    useEffect(() => {
        if (!isVisionEnabled) {
            setHasGreeted(false);
        }
    }, [isVisionEnabled]);

    useEffect(() => {
        responseEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [response]);

    // Speak the latest response when it changes
    useEffect(() => {
        if (response.length > 0 && isSpeakingEnabled) {
            const latestResponse = response[response.length - 1].answer;
            setNotification('Justin Panther is speaking...');
            speak(latestResponse, () => {
                setIsSpeakingEnabled(false);
            });
            setTimeout(() => {
                setNotification('');
            }, 5000);
        }
    }, [response, speak, isSpeakingEnabled]);

    const handleSubmit = async (e, promptText = null) => {
        if (e && e.preventDefault) e.preventDefault();
        const currentPrompt = promptText || prompt;
        if (!currentPrompt.trim()) return;

        setIsLoading(true);

        try {
            await processBatch([], currentPrompt);
        } catch (error) {
            console.error('Error processing prompt:', error);
            alert(`Error: ${error.message}`);
        }

        setIsLoading(false);
        setPrompt('');
    };

    const processBatch = async (batchChunks, initialPrompt) => {
        const apiEndpoint = `${process.env.REACT_APP_OPENAI_ENDPOINT}/openai/deployments/${process.env.REACT_APP_OPENAI_DEPLOYMENT}/chat/completions?api-version=2024-08-01-preview`;
        const headers = {
            'api-key': process.env.REACT_APP_OPENAI_API_KEY,
            'Content-Type': 'application/json',
        };

        const pastMessages = response
            .slice(-chatConfig.chatParameters.pastMessagesToInclude)
            .map((msg) => [
                { role: 'user', content: msg.question },
                { role: 'assistant', content: msg.answer },
            ])
            .flat();

        const systemMessage = {
            role: 'system',
            content:
                'You are Justin Panther, a powerful LLM peopleoid in the form of a space Panther, performing the role of a mentor. Give succinct, brief answers until you are sure the participant has time to engage in details, because you are supporting the students and teachers at the Forest Park Middle School Nyx NoCode Hackathon Demo on March 4-5, 2025. This demo brings AI-powered learning to Math classes on March 4 and Science classes on March 5. Your goal is to encourage and mentor students as they use Nyx NoCode, a no-code AI development platform created by Karen Kilroy, to build innovative "AI for Good" projects.' +
                'Welcome each student with a warm, encouraging attitude. When students approach, introduce them to the Nyx NoCode Hackathon Demo and offer tips on how they can use Nyx NoCode to bring their ideas to life. Be positive and reassuring, reminding them that creating with Nyx NoCode is as easy as describing what they want to build.' +
                'Let them know that with Nyx NoCode, they can click the Create Now button to go to the Studio, where they can create websites, interactive apps, and graphics, all without needing to write any code. Explain that Nyx NoCode uses prompt-driven design: they simply describe what they want to create, and Nyx generates it on the screen within seconds! It\'s about turning ideas into real digital creations, which is great practice for prompt engineering—a skill that is becoming more valuable in tech careers.' +
                'Introduce the students to the Nyx NoCode Doodle Pad, a tool where their creativity can be unleashed! Tell them, "The Doodle Pad lets you express your ideas visually. If words aren\'t your thing, try drawing your ideas instead!" Explain that the Doodle Pad is packed with options:' +
                'Guide them through the following schedule for the demo:' +
                '1. Complete the 10-question pre-assessment (30 seconds per question)' +
                '2. Log into Nyx NoCode and meet Justin Panther (that\'s you!)' +
                '3. Learn about the "AI for Good" hacktivity' +
                '4. Explore help videos and instructions' +
                '5. Create in the Nyx NoCode Studio by clicking the Create Now button' +
                '6. Complete a post-assessment when time is up' +
                '7. Share your favorite creation with your teacher' +
                'Encourage students to think about how AI can be used for good - helping solve problems, making life better, or creating positive experiences. For Math classes, suggest projects that might visualize mathematical concepts, create interactive problem solvers, or demonstrate real-world applications of math. For Science classes, suggest projects related to environmental monitoring, scientific visualization, or exploration tools.' +
                'Emphasize that this demo is their first step in a larger journey of the 30 Day NYX NoCode Hackathon program that will follow. Their projects and feedback today will help shape that longer experience.' +
                'Throughout the demo, keep cheering on the students and offering practical advice when they get stuck. Always be upbeat and remind them that learning is a journey. To finish, tell them, "I can\'t wait to see what you create using AI for Good!"',
        };

        const messages = [
            systemMessage,
            ...pastMessages,
            { role: 'user', content: initialPrompt },
        ];

        const data = {
            model: 'gpt-4o-2',
            messages: messages,
            max_tokens: 1024 - messages.length * 20,
            temperature: chatConfig.chatParameters.temperature,
            top_p: chatConfig.chatParameters.top_p,
            frequency_penalty: chatConfig.chatParameters.frequencyPenalty,
            presence_penalty: chatConfig.chatParameters.presencePenalty,
        };

        try {
            const apiResponse = await axios.post(apiEndpoint, data, { headers });
            const gptResponse = apiResponse.data.choices[0].message.content;
            setResponse((prevResponses) => [
                ...prevResponses,
                { question: initialPrompt, answer: gptResponse },
            ]);
            interpretAndActOnGPTResponse(gptResponse);
        } catch (error) {
            console.error('Error with OpenAI Chat:', error.response ? error.response.data : error.message);
            alert(`Error: ${error.response ? JSON.stringify(error.response.data) : error.message}`);
        }
    };

    const interpretAndActOnGPTResponse = (gptResponse) => {
        const lowerCaseResponse = gptResponse.toLowerCase();

        if (lowerCaseResponse.includes('image') || lowerCaseResponse.includes('photo')) {
            setFilterCriteria({ type: 'image' });
        } else if (lowerCaseResponse.includes('audio') || lowerCaseResponse.includes('music')) {
            setFilterCriteria({ type: 'audio' });
        } else if (lowerCaseResponse.includes('document')) {
            setFilterCriteria({ type: 'document' });
        } else if (lowerCaseResponse.includes('video')) {
            setFilterCriteria({ type: 'video' });
        } else {
            setFilterCriteria({});
        }
    };

    const handleClearChat = () => {
        setResponse([]);
        setPrompt('');
    };

    const toggleMute = () => {
        setIsMuted((prevMuted) => !prevMuted);
        setIsSpeakingEnabled((prev) => !prev);
    };

    const readAndProcessFile = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = async (event) => {
                const fileContent = event.target.result;
                await processBatch([fileContent], file.name);
                resolve();
            };
            reader.onerror = reject;
            reader.readAsText(file);
        });
    };

    return (
        <div className="chat-container">
            <button className="chat-toggle-button" onClick={toggleChat}>
                {isOpen ? '🔉 Hide Justin Panther\'s Chatbox 🐾' : '🔉 Show Justin Panther\'s Chatbox 🐾'}
            </button>
            <SpeechControls
                speechRecognitionSupported={speechRecognitionSupported}
                isSpeaking={isSpeaking}
            />
            {isOpen && (
                <DragAndDropArea
                    dragOver={dragOver}
                    handleDragOver={(e) => {
                        e.preventDefault();
                        setDragOver(true);
                    }}
                    handleDragLeave={() => setDragOver(false)}
                    handleDrop={async (e) => {
                        e.preventDefault();
                        setDragOver(false);
                        const files = e.dataTransfer.files;
                        if (files.length > 0) {
                            for (const file of files) {
                                await readAndProcessFile(file);
                            }
                        }
                    }}
                    chatPopupRef={chatPopupRef}
                >
                    <div className="chatbox-container">
                        <div className="chat-title-bar">
                            <div className="toggles-container">
                                <label className="switch">
                                    <input
                                        type="checkbox"
                                        checked={isVisionEnabled}
                                        onChange={() => setIsVisionEnabled(!isVisionEnabled)}
                                    />
                                    <span className="slider round"></span>
                                </label>
                                <span className="toggle-label">Vision</span>
                            </div>
                        </div>

                        {isVisionEnabled && (
                            <WebcamComponent onPersonDetected={handlePersonDetected} />
                        )}

                        <LoadingOverlay isLoading={isLoading} />

                        <button className="mute-button" onClick={toggleMute}>
                            {isMuted ? "🔊 Unmute Justin Panther 🐾" : " 🔇 Mute Justin Panther 🐾"}
                        </button>

                        <div className="response-container">
                            {response.map((exchange, index) => (
                                <ChatMessage
                                    key={index}
                                    exchange={exchange}
                                    isEditMode={isEditMode}
                                    editableQA={editableQA}
                                    handleEditQA={(qa) => {
                                        setEditableQA(qa);
                                        setIsEditMode(true);
                                    }}
                                    handleQAChange={(e) => {
                                        const { name, value } = e.target;
                                        setEditableQA((prevQA) => ({
                                            ...prevQA,
                                            [name]: value,
                                        }));
                                    }}
                                    handleSaveEditedQA={() => {
                                        setResponse((prevResponses) =>
                                            prevResponses.map((qa) =>
                                                qa.question === editableQA.question ? editableQA : qa
                                            )
                                        );
                                        setIsEditMode(false);
                                        setNotification('Response Edited');
                                        setTimeout(() => setNotification(''), 3000);
                                    }}
                                />
                            ))}
                            <div ref={responseEndRef} />
                        </div>
                        <ChatInput
                            prompt={prompt}
                            handleInputChange={handleInputChange}
                            handleSubmit={handleSubmit}
                            handleClearChat={handleClearChat}
                            handleDownloadMemories={() =>
                                alert('Contact my designer, karen@kilroyai.com, to enable Memories.')
                            }
                            chunks={[]}
                            currentChunkIndex={0}
                            handleNextChunk={() => {}}
                        />

                        {isSpeaking && (
                            <div className="speaking-indicator">Generating Response...</div>
                        )}
                    </div>
                </DragAndDropArea>
            )}
            {notification && <Notification message={notification} />}
        </div>
    );
};

export default Chatbot;
